import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { BarChart3, Eye, Clock, MousePointerClick, Sparkles, Users } from "lucide-react";

const STATS = [[Eye, "Tour views", 4812, ""], [Clock, "Avg. time in tour", 7.4, " min"], [MousePointerClick, "Hotspot taps", 12960, ""], [Users, "Leads captured", 186, ""]];
const HOTSPOTS = [["Kitchen & Family Room", 100], ["Primary ensuite", 78], ["Kitchen countertops", 64], ["Basement", 51], ["Built-in oven", 33]];
const UPGRADES = [["Quartz countertops", 142, "+$3,200"], ["Wide-plank oak floors", 97, "+$4,800"], ["Graphite cabinets", 61, "+$3,100"]];
const DAYS = [32, 41, 38, 55, 48, 62, 71, 66, 80, 74, 92, 88, 104, 97];

function Count({ to, on, decimals = 0 }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!on) { setV(0); return undefined; }
    let raf, start;
    const step = (t) => { start = start || t; const k = Math.min(1, (t - start) / 1400); setV(to * (1 - (1 - k) ** 3)); if (k < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, on]);
  return <>{v.toLocaleString("en-CA", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}</>;
}

const Bar = ({ w, on, delay, className }) => (
  <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><motion.div className={`h-full rounded-full ${className}`} initial={{ width: 0 }} animate={{ width: on ? `${w}%` : 0 }} transition={{ duration: 1, delay, ease: [0.2, 0.7, 0.2, 1] }} /></div>
);

function Dashboard() {
  const ref = useRef(null);
  const on = useInView(ref, { amount: 0.35 });
  const max = Math.max(...DAYS);
  return (
    <div ref={ref} className="rounded-3xl bg-white text-slate-900 shadow-2xl p-5 sm:p-7" data-testid="analytics-showcase-dashboard">
      <div className="flex items-center justify-between">
        <p className="font-serif text-xl">Hearthstone Homes · Last 14 days</p>
        <span className="text-[10px] uppercase tracking-widest rounded-full bg-slate-100 text-slate-500 px-2 py-1">Sample data</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
        {STATS.map(([Icon, label, n, unit], i) => (
          <div key={label} className="rounded-2xl border p-4" data-testid={`analytics-stat-${i}`}>
            <Icon className="w-4 h-4 text-[#A87A12]" />
            <p className="font-serif text-2xl sm:text-3xl mt-2 tabular-nums"><Count to={n} on={on} decimals={unit ? 1 : 0} />{unit}</p>
            <p className="text-xs text-slate-500">{label}</p>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-5 mt-5">
        <div className="rounded-2xl border p-4">
          <p className="text-xs font-semibold text-slate-500 mb-3">Daily tour views</p>
          <div className="flex items-end gap-1.5 h-28">
            {DAYS.map((d, i) => <motion.div key={i} className="flex-1 rounded-t-md bg-[#0F2A47]" initial={{ height: 0 }} animate={{ height: on ? `${(d / max) * 100}%` : 0 }} transition={{ duration: 0.8, delay: i * 0.04 }} />)}
          </div>
        </div>
        <div className="rounded-2xl border p-4">
          <p className="text-xs font-semibold text-slate-500 mb-3">Most-tapped hotspots</p>
          <div className="space-y-2.5">
            {HOTSPOTS.map(([n, w], i) => <div key={n}><p className="text-xs mb-1">{n}</p><Bar w={w} on={on} delay={i * 0.08} className="bg-[#D4A12A]" /></div>)}
          </div>
        </div>
      </div>
      <div className="rounded-2xl border p-4 mt-5">
        <p className="text-xs font-semibold text-slate-500 mb-3 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-[#A87A12]" />Top upgrade picks</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {UPGRADES.map(([n, c, p]) => <div key={n} className="rounded-xl bg-[#FAF8F5] p-3"><p className="text-sm font-medium">{n}</p><p className="text-xs text-slate-500">{c} buyers · {p}</p></div>)}
        </div>
      </div>
    </div>
  );
}

const POINTS = [
  ["Who's looking, and at what", "Tour views, time in tour and the rooms and hotspots each visitor taps."],
  ["Leads scored for you", "Every lead is rated hot, warm or cold from what they did in the tour, with the reasons shown. Sales get an email the moment one turns hot."],
  ["Upgrade picks", "Know which finishes buyers choose, and price your upgrades with confidence."],
  ["Views to leads", "Track how many tours turn into enquiries, quotes and booked viewings."],
];

export function AnalyticsShowcase() {
  return (
    <section className="relative z-10 bg-[#0F2A47] text-white py-24" data-testid="landing-analytics-showcase">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.85fr_1.15fr] gap-12 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#E9C25A] flex items-center gap-2"><BarChart3 className="w-4 h-4" />Lead generation built in</p>
          <h2 className="font-serif text-3xl sm:text-5xl font-semibold mt-3 leading-tight">A tour that finds your buyers for you.</h2>
          <p className="text-white/60 mt-4 max-w-xl">Every tour is a lead generator. See who's clicking, what they're looking at and what they upgrade, so your team calls the serious buyers first.</p>
          <div className="mt-8 space-y-5">
            {POINTS.map(([t, d], i) => (
              <div key={t} className="flex gap-3" data-testid={`analytics-point-${i}`}>
                <span className="mt-1.5 w-2 h-2 rounded-full bg-[#D4A12A] shrink-0" />
                <div><p className="font-semibold text-sm">{t}</p><p className="text-sm text-white/60">{d}</p></div>
              </div>
            ))}
          </div>
        </div>
        <Dashboard />
      </div>
    </section>
  );
}
